import { Stack, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useFormikContext, getIn } from "formik";
import { hasMissingAssessmentText } from "../../../../../formatting/sanitise-rich-text";
import type { EditSolutionFormValues } from "../../../../../edit/form/types";
import BenefitsContainer from "../../../../../form/components/benefits";
import ScoresContainer from "../../../../../form/components/scores";
import ShortDescriptionField from "../../../../../form/components/ShortDescriptionField";
import SDGSelect from "../../../../../form/components/SDGSelect";
import LocationPinMapLinkField from "../../../../../form/components/LocationPinMapLinkField";
import IntegrityScores from "../../../../../form/components/integrity-scores";
import UniqueBenefitsInputs from "../../../../../form/components/unique-benefits-table";
import SatelliteImageryContainer from "../../../../../form/components/satellite-imagery";
import { FormikTextField } from "../../../../../form/components/narrative/EditableFields";
import type {
  AssessmentReportSectionEditFieldsProps,
  AssessmentRichTextFieldName,
} from "../../../types";

function AssessmentReportSectionEditInputs({
  section,
}: AssessmentReportSectionEditFieldsProps) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { values, errors } = useFormikContext<EditSolutionFormValues>();
  if (section === "images")
    return (
      <Stack spacing={3}>
        <LocationPinMapLinkField />
        <SatelliteImageryContainer />
      </Stack>
    );

  if (section === "text") {
    const fields: Array<{ label: string; name: AssessmentRichTextFieldName }> =
      [
        { label: "SDG information", name: "assessmentReport.sdgInfo" },
        {
          label: "Developer information",
          name: "assessmentReport.developerInfo",
        },
        {
          label: "Monitoring information",
          name: "assessmentReport.monitoringInfo",
        },
        {
          label: "Location information",
          name: "assessmentReport.locationInfo",
        },
      ];
    const tabs = [
      { label: "About", name: "solution-short-description" },
      ...fields,
    ];
    return (
      <Stack spacing={3}>
        <Tabs
          value={selectedTab}
          onChange={(_event, value: number) => setSelectedTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Edit assessment report text tabs"
        >
          {tabs.map(({ name, label }, index) => {
            const value = getIn(values, name);
            const invalid =
              Boolean(getIn(errors, name)) ||
              hasMissingAssessmentText(value, index !== 0);
            return (
              <Tab
                key={name}
                label={label}
                sx={{ color: invalid ? "error.main" : undefined }}
              />
            );
          })}
        </Tabs>
        {selectedTab === 0 ? (
          <ShortDescriptionField />
        ) : (
          <FormikTextField
            key={fields[selectedTab - 1].name}
            name={fields[selectedTab - 1].name}
            id={fields[selectedTab - 1].name.replace(/\./g, "-")}
            label={fields[selectedTab - 1].label}
            helperText="Paragraphs, lists and links are supported."
          />
        )}
      </Stack>
    );
  }

  if (section === "data")
    return (
      <Stack spacing={3}>
        <ScoresContainer open />
        <BenefitsContainer open />
        <SDGSelect />
        <UniqueBenefitsInputs />
        <IntegrityScores />
      </Stack>
    );
  return null;
}

export default AssessmentReportSectionEditInputs;
